/**
 * Keyboard actions of the portal, named after the Oracle Forms functions users
 * know. Defaults follow the Forms key layout; the company layout
 * (Administration > Keyboard Shortcuts) can change any of them.
 *
 * `generic` actions work on every screen (field-level behaviour); the rest do
 * something only where a screen says what they mean (useShortcutActions).
 */
export type ShortcutAction =
  | 'COMMIT'
  | 'EXIT'
  | 'CLEAR_FIELD'
  | 'CLEAR_RECORD'
  | 'INSERT_RECORD'
  | 'DELETE_RECORD'
  | 'DUPLICATE_RECORD'
  | 'DUPLICATE_FIELD'
  | 'NEXT_RECORD'
  | 'PREVIOUS_RECORD'
  | 'NEXT_BLOCK'
  | 'PREVIOUS_BLOCK'
  | 'LIST_OF_VALUES'
  | 'ENTER_QUERY'
  | 'EXECUTE_QUERY'
  | 'COUNT_QUERY'
  | 'EDIT'
  | 'UPDATE_RECORD'
  | 'PRINT'
  | 'LIST_TAB_PAGES'
  | 'BLOCK_MENU'
  | 'DISPLAY_ERROR'
  | 'SHOW_KEYS'
  | 'MENU_SEARCH'
  | 'HELP';

export interface ShortcutActionDef {
  id: ShortcutAction;
  label: string;
  description: string;
  /** Oracle Forms default */
  defaultKeys: string;
  group: 'Save & exit' | 'Records' | 'Fields & blocks' | 'Query' | 'Other';
  generic?: boolean;
}

export const SHORTCUT_ACTIONS: ShortcutActionDef[] = [
  { id: 'COMMIT', label: 'Commit (Save)', description: 'Save the current entry', defaultKeys: 'F10', group: 'Save & exit' },
  { id: 'EXIT', label: 'Exit', description: 'Leave the screen (asks before losing changes)', defaultKeys: 'F4', group: 'Save & exit' },
  { id: 'CLEAR_RECORD', label: 'Clear Record', description: 'Clear the current line or entry', defaultKeys: 'F6', group: 'Records' },
  { id: 'INSERT_RECORD', label: 'Insert Record', description: 'Start a new line', defaultKeys: 'Ctrl+Down', group: 'Records' },
  { id: 'DELETE_RECORD', label: 'Delete Record', description: 'Delete the current line', defaultKeys: 'Ctrl+Up', group: 'Records' },
  { id: 'DUPLICATE_RECORD', label: 'Duplicate Record', description: 'Copy the previous line', defaultKeys: 'Shift+F6', group: 'Records' },
  { id: 'NEXT_RECORD', label: 'Next Record', description: 'Move to the next line', defaultKeys: 'Down', group: 'Records' },
  { id: 'PREVIOUS_RECORD', label: 'Previous Record', description: 'Move to the previous line', defaultKeys: 'Up', group: 'Records' },
  { id: 'UPDATE_RECORD', label: 'Update Record', description: 'Edit the current line', defaultKeys: 'Ctrl+U', group: 'Records' },
  { id: 'LIST_OF_VALUES', label: 'List of Values', description: 'Open the list of the current field', defaultKeys: 'F9', group: 'Fields & blocks', generic: true },
  { id: 'CLEAR_FIELD', label: 'Clear Field', description: 'Clear the current field', defaultKeys: 'F5', group: 'Fields & blocks', generic: true },
  { id: 'DUPLICATE_FIELD', label: 'Duplicate Field', description: 'Copy the field value from the previous line', defaultKeys: 'Shift+F5', group: 'Fields & blocks' },
  { id: 'NEXT_BLOCK', label: 'Next Block', description: 'Jump to the next section', defaultKeys: 'Shift+PageDown', group: 'Fields & blocks', generic: true },
  { id: 'PREVIOUS_BLOCK', label: 'Previous Block', description: 'Jump to the previous section', defaultKeys: 'Shift+PageUp', group: 'Fields & blocks', generic: true },
  { id: 'EDIT', label: 'Edit', description: 'Open the current field in an editor', defaultKeys: 'Ctrl+E', group: 'Fields & blocks' },
  { id: 'ENTER_QUERY', label: 'Enter Query', description: 'Go to the search / filter fields', defaultKeys: 'F7', group: 'Query' },
  { id: 'EXECUTE_QUERY', label: 'Execute Query', description: 'Run the search', defaultKeys: 'F8', group: 'Query' },
  { id: 'COUNT_QUERY', label: 'Count Query', description: 'Count the matching records', defaultKeys: 'F12', group: 'Query' },
  { id: 'PRINT', label: 'Print', description: 'Print the current document', defaultKeys: 'Ctrl+P', group: 'Other' },
  { id: 'LIST_TAB_PAGES', label: 'List Tab Pages', description: 'Switch between tabs of the screen', defaultKeys: 'F2', group: 'Other' },
  { id: 'BLOCK_MENU', label: 'Block Menu', description: 'List the sections of the screen', defaultKeys: 'Ctrl+B', group: 'Other' },
  { id: 'DISPLAY_ERROR', label: 'Display Error', description: 'Show the last error again', defaultKeys: 'Shift+Ctrl+E', group: 'Other' },
  { id: 'SHOW_KEYS', label: 'Show Keys', description: 'List the keys of this screen', defaultKeys: 'Ctrl+K', group: 'Other', generic: true },
  {
    id: 'MENU_SEARCH',
    label: 'Menu Search',
    description: 'Go to the menu search (then arrows and Enter to open a page)',
    defaultKeys: 'Ctrl+M',
    group: 'Other',
    generic: true,
  },
  { id: 'HELP', label: 'Help', description: 'Open the help of the screen', defaultKeys: 'Ctrl+H', group: 'Other' },
];

export const ACTION_BY_ID = Object.fromEntries(SHORTCUT_ACTIONS.map((a) => [a.id, a])) as Record<
  ShortcutAction,
  ShortcutActionDef
>;

export const FORMS_DEFAULTS = Object.fromEntries(SHORTCUT_ACTIONS.map((a) => [a.id, a.defaultKeys])) as Record<
  ShortcutAction,
  string | null
>;

/** fired by the Menu Search key; the sidebar (Navigation) opens and focuses its search */
export const MENU_SEARCH_EVENT = 'erp:menu-search';
